import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, TextField, Chip, Checkbox, CircularProgress, Alert,
  List, ListItem, ListItemAvatar, ListItemText, Avatar, Paper, Tabs, Tab
} from '@mui/material';
import {
  Close, Send, Email, Sms, WhatsApp, Person, Group, Search,
  CheckCircle, Schedule, LocalOffer, ContentCopy
} from '@mui/icons-material';
import axios from 'axios';
import { HEADERS } from '../../../API/UrlAndPaths';
import Swal from 'sweetalert2';
import KooppaiIcon from './KooppaiIcon';
import { jwtDecode } from 'jwt-decode';

const SendOfferDialog = ({ open, onClose, offer }) => {
  const [tabValue, setTabValue] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyInfo, setCompanyInfo] = useState({ name: '', orderUrl: '' });
  
  const [sendOptions, setSendOptions] = useState({
    channel: 'WHATSAPP',
    customMessage: '',
    sendNow: true,
    scheduleDate: ''
  });

  // Get company info from token and settings
  const getCompanyInfo = async () => {
    try {
      const token = localStorage.getItem('userToken');
      if (token) {
        const decoded = jwtDecode(token);
        // Try to get company settings
        const res = await axios.get('api/settings/company', HEADERS);
        if (res.status === 200 && res.data) {
          return {
            name: res.data.companyName || res.data.name || decoded?.admin || 'Our Restaurant',
            orderUrl: res.data.orderUrl || res.data.websiteUrl || `${window.location.origin}/order`
          };
        }
        return {
          name: decoded?.admin || 'Our Restaurant',
          orderUrl: `${window.location.origin}/order`
        };
      }
    } catch (e) {
      console.log('Could not fetch company info:', e);
    }
    return { name: 'Our Restaurant', orderUrl: `${window.location.origin}/order` };
  };

  // Build default offer message with company name and order link
  const buildDefaultMessage = (company) => {
    if (!offer) return '';
    
    const companyName = company?.name || companyInfo.name || 'Our Restaurant';
    const orderUrl = company?.orderUrl || companyInfo.orderUrl || `${window.location.origin}/order`;
    
    let message = `🏪 *${companyName}*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `🎉 *${offer.title}*\n\n`;
    
    if (offer.description) {
      message += `${offer.description}\n\n`;
    }
    
    if (offer.type === 'PERCENTAGE') {
      message += `💰 Get *${offer.value}% OFF*\n`;
    } else if (offer.type === 'FIXED_AMOUNT') {
      message += `💰 Get *₹${offer.value} OFF*\n`;
    } else if (offer.type === 'FREE_DELIVERY') {
      message += `🚚 *FREE DELIVERY*\n`;
    }
    
    if (offer.minOrderValue) {
      message += `📋 Min order: ₹${offer.minOrderValue}\n`;
    }
    
    if (offer.code) {
      message += `\n🏷️ *Promo Code:* \`${offer.code}\`\n`;
    }
    
    if (offer.endDate) {
      message += `⏰ Valid till: ${new Date(offer.endDate).toLocaleDateString()}\n`;
    }
    
    message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    message += `👉 *Order Now:*\n${orderUrl}\n`;
    message += `━━━━━━━━━━━━━━━━━━━━`;
    
    return message;
  };

  useEffect(() => {
    if (open) {
      fetchCustomers();
      // Fetch company info and build message
      getCompanyInfo().then(company => {
        setCompanyInfo(company);
        setSendOptions(prev => ({
          ...prev,
          customMessage: buildDefaultMessage(company)
        }));
      });
      setSelectedCustomers([]);
      setTabValue(0);
    }
  }, [open, offer]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.post('account/viewCustomerForDropdown', {}, HEADERS);
      if (res.status === 200) {
        setCustomers(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers);
    }
  };

  const handleCustomerToggle = (customer) => {
    const isSelected = selectedCustomers.some(c => c._id === customer._id);
    if (isSelected) {
      setSelectedCustomers(prev => prev.filter(c => c._id !== customer._id));
    } else {
      setSelectedCustomers(prev => [...prev, customer]);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const search = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(search) ||
      c.mobileNo?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  const copyOfferCode = () => {
    if (offer?.code) {
      navigator.clipboard.writeText(offer.code);
      Swal.fire({ title: 'Code Copied!', icon: 'success', timer: 1500, showConfirmButton: false });
    }
  };

  const handleSend = async () => {
    if (selectedCustomers.length === 0) {
      Swal.fire('Error', 'Please select at least one customer', 'error');
      return;
    }

    setSending(true);
    try {
      const payload = {
        offerId: offer._id,
        offerTitle: offer.title,
        customers: selectedCustomers.map(c => ({
          _id: c._id,
          name: c.name,
          mobileNo: c.mobileNo,
          email: c.email
        })),
        channel: sendOptions.channel,
        message: sendOptions.customMessage,
        scheduleDate: sendOptions.sendNow ? null : sendOptions.scheduleDate
      };

      const res = await axios.post(`/api/crm/offers/${offer._id}/send`, payload, HEADERS);
      
      if (res.status === 200 || res.data.status === 200) {
        const result = res.data.data;
        if (result.failed > 0) {
          Swal.fire({
            title: 'Partially Sent',
            html: `<p>Sent: ${result.sent} | Failed: ${result.failed}</p>${result.errors ? `<p style="color:#d32f2f;font-size:12px">${result.errors.slice(0, 3).join('<br>')}</p>` : ''}`,
            icon: 'warning'
          });
        } else {
          Swal.fire('Success', `Offer sent to ${result.sent} customer(s) via ${sendOptions.channel}!`, 'success');
        }
        onClose();
        setSelectedCustomers([]);
      } else {
        throw new Error(res.data?.data || 'Failed to send');
      }
    } catch (error) {
      console.error('Send error:', error);
      Swal.fire('Error', error.response?.data?.data || 'Failed to send offer', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOffer color="primary" />
          <Box>
            <Typography variant="h6">Send Offer</Typography>
            <Typography variant="body2" color="textSecondary">{offer?.title}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Group />} label="Select Customers" iconPosition="start" />
          <Tab icon={<Send />} label="Send Options" iconPosition="start" />
        </Tabs>

        {/* Tab 0: Customer Selection */}
        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            {/* Offer Summary */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" color="success.dark">Offer Details</Typography>
                  <Typography variant="body2">
                    {offer?.type === 'PERCENTAGE' && `${offer?.value}% OFF`}
                    {offer?.type === 'FIXED_AMOUNT' && `₹${offer?.value} OFF`}
                    {offer?.type === 'FREE_DELIVERY' && 'FREE DELIVERY'}
                    {offer?.minOrderValue > 0 && ` (Min: ₹${offer?.minOrderValue})`}
                  </Typography>
                </Box>
                {offer?.code && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={offer.code} 
                      color="success" 
                      sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                    />
                    <IconButton size="small" onClick={copyOfferCode}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Search & Select All */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search customers by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} /> }}
              />
              <Button 
                variant="outlined" 
                onClick={handleSelectAll}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0 ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            {/* Selected Count */}
            {selectedCustomers.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedCustomers.length} customer(s) selected
              </Alert>
            )}

            {/* Customer List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {filteredCustomers.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No customers found" secondary="Try a different search term" />
                    </ListItem>
                  ) : (
                    filteredCustomers.map((customer) => {
                      const isSelected = selectedCustomers.some(c => c._id === customer._id);
                      return (
                        <ListItem
                          key={customer._id}
                          button
                          onClick={() => handleCustomerToggle(customer)}
                          selected={isSelected}
                          sx={{ 
                            borderBottom: '1px solid #eee',
                            bgcolor: isSelected ? 'primary.light' : 'inherit',
                            '&:hover': { bgcolor: isSelected ? 'primary.light' : 'action.hover' }
                          }}
                        >
                          <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>
                              {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {customer.name || 'Unknown'}
                                {!customer.mobileNo && (
                                  <Typography variant="caption" sx={{ color: '#f57c00', bgcolor: '#fff3e0', px: 0.5, borderRadius: 0.5, fontSize: '10px' }}>
                                    No Phone
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {customer.mobileNo && <Typography variant="caption" display="block">{customer.mobileNo}</Typography>}
                                {customer.email && <Typography variant="caption" display="block" sx={{ color: 'success.main' }}>{customer.email}</Typography>}
                              </Box>
                            }
                          />
                          {isSelected && <CheckCircle color="primary" />}
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {/* Tab 1: Send Options */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            {/* Channel Selection */}
            <Typography variant="subtitle2" gutterBottom>Send Via</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {[
                { value: 'KOOPPAI', label: 'KooppAI', icon: <KooppaiIcon />, color: '#6366f1' },
                { value: 'WHATSAPP', label: 'WhatsApp', icon: <WhatsApp />, color: '#25D366' },
                { value: 'SMS', label: 'SMS', icon: <Sms />, color: '#2196f3' },
                { value: 'EMAIL', label: 'Email', icon: <Email />, color: '#ea4335' },
              ].map(channel => (
                <Chip
                  key={channel.value}
                  icon={channel.icon}
                  label={channel.label}
                  onClick={() => setSendOptions(prev => ({ ...prev, channel: channel.value }))}
                  variant={sendOptions.channel === channel.value ? 'filled' : 'outlined'}
                  color={sendOptions.channel === channel.value ? 'primary' : 'default'}
                  sx={{ 
                    px: 1,
                    cursor: 'pointer',
                    ...(sendOptions.channel === channel.value && { bgcolor: channel.color, color: '#fff' })
                  }}
                />
              ))}
            </Box>

            {/* Custom Message */}
            <Typography variant="subtitle2" gutterBottom>Message Preview</Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={sendOptions.customMessage}
              onChange={(e) => setSendOptions(prev => ({ ...prev, customMessage: e.target.value }))}
              placeholder="Enter your message..."
              helperText="Use {{name}} to personalize with customer name"
              sx={{ mb: 3 }}
            />

            {/* Schedule Options */}
            <Typography variant="subtitle2" gutterBottom>When to Send</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                icon={<Send />}
                label="Send Now"
                onClick={() => setSendOptions(prev => ({ ...prev, sendNow: true }))}
                variant={sendOptions.sendNow ? 'filled' : 'outlined'}
                color={sendOptions.sendNow ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                icon={<Schedule />}
                label="Schedule"
                onClick={() => setSendOptions(prev => ({ ...prev, sendNow: false }))}
                variant={!sendOptions.sendNow ? 'filled' : 'outlined'}
                color={!sendOptions.sendNow ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
            
            {!sendOptions.sendNow && (
              <TextField
                type="datetime-local"
                fullWidth
                label="Schedule Date & Time"
                value={sendOptions.scheduleDate}
                onChange={(e) => setSendOptions(prev => ({ ...prev, scheduleDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            )}

            {/* Summary */}
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Summary</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip icon={<Person />} label={`${selectedCustomers.length} recipients`} size="small" />
                <Chip 
                  icon={
                    sendOptions.channel === 'KOOPPAI' ? <KooppaiIcon /> : 
                    sendOptions.channel === 'WHATSAPP' ? <WhatsApp /> : 
                    sendOptions.channel === 'SMS' ? <Sms /> : <Email />
                  } 
                  label={sendOptions.channel} 
                  size="small"
                  sx={{ 
                    bgcolor: sendOptions.channel === 'WHATSAPP' ? '#25D366' : 
                             sendOptions.channel === 'EMAIL' ? '#ea4335' : 
                             sendOptions.channel === 'SMS' ? '#2196f3' : '#6366f1',
                    color: '#fff'
                  }}
                />
                <Chip icon={sendOptions.sendNow ? <Send /> : <Schedule />} label={sendOptions.sendNow ? 'Send Now' : 'Scheduled'} size="small" />
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
          onClick={handleSend}
          disabled={sending || selectedCustomers.length === 0}
          sx={{ 
            bgcolor: sendOptions.channel === 'WHATSAPP' ? '#25D366' : 'primary.main',
            '&:hover': { bgcolor: sendOptions.channel === 'WHATSAPP' ? '#128C7E' : 'primary.dark' }
          }}
        >
          {sending ? 'Sending...' : `Send to ${selectedCustomers.length} Customer(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendOfferDialog;
